import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const quarterlyData = [
  { quarter: "Q1 2024", score: 62, challenges: 8, litigation: 5 },
  { quarter: "Q2 2024", score: 65, challenges: 10, litigation: 7 },
  { quarter: "Q3 2024", score: 70, challenges: 12, litigation: 6 },
  { quarter: "Q4 2024", score: 68, challenges: 9, litigation: 8 },
  { quarter: "Q1 2025", score: 74, challenges: 14, litigation: 9 },
  { quarter: "Q2 2025", score: 78, challenges: 16, litigation: 11 },
  { quarter: "Q3 2025", score: 76, challenges: 13, litigation: 10 },
  { quarter: "Q4 2025", score: 80, challenges: 15, litigation: 12 },
  { quarter: "Q1 2026", score: 82, challenges: 17, litigation: 13 },
];

const currentScore = 82;
const getLevel = (s: number) => {
  if (s >= 80) return { label: "High Enforcement Environment", color: "text-red-600", bg: "bg-red-100" };
  if (s >= 65) return { label: "Elevated Enforcement Risk", color: "text-orange-600", bg: "bg-orange-100" };
  if (s >= 45) return { label: "Moderate Enforcement Risk", color: "text-amber-600", bg: "bg-amber-100" };
  return { label: "Low Enforcement Risk", color: "text-emerald-600", bg: "bg-emerald-100" };
};

const level = getLevel(currentScore);

const factors = [
  { factor: "Merger Challenges", detail: "17 merger challenges in Q1 2026, a significant increase over prior quarters." },
  { factor: "Major Litigation", detail: "Several high-profile monopolization cases advancing through federal courts." },
  { factor: "Policy Guidance", detail: "Updated Merger Guidelines expanding concentration thresholds." },
  { factor: "Enforcement Priorities", detail: "Heightened focus on technology, healthcare, and pharmaceutical sectors." },
  { factor: "Legislative Developments", detail: "Bipartisan antitrust reform legislation advancing in Congress." },
];

const EnforcementIndex = () => (
  <Layout>
    <PageHead title="Antitrust Enforcement Risk Index" description="Quarterly antitrust enforcement climate index by Alicia J. Batts with visual charts, key enforcement factors, and strategic implications for corporate decision-makers." path="/enforcement-index" keywords="antitrust enforcement index, Alicia Batts enforcement analysis, FTC enforcement climate, DOJ antitrust trends, competition enforcement risk" />
    <section className="bg-hero text-primary-foreground py-20">
      <div className="editorial-container">
          <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
          <AnimatedSection animation="fade-up" delay={100}>
            <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Antitrust Enforcement Risk Index</h1>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={200}>
            <p className="text-lg opacity-80 max-w-2xl">Quarterly assessment of the overall antitrust enforcement climate for corporate strategy and transaction planning.</p>
          </AnimatedSection>
        </div>
    </section>

    <section className="section-padding">
      <div className="editorial-container">
        {/* Current Score */}
        <AnimatedSection animation="scale">
        <div className="bg-card rounded-lg p-10 shadow-elevated border border-border text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-4">Q1 2026 Enforcement Risk Score</p>
          <div className="text-7xl font-serif font-bold text-foreground mb-3">{currentScore}</div>
          <span className={`text-sm font-semibold px-4 py-1.5 rounded-full ${level.bg} ${level.color}`}>{level.label}</span>
          <p className="text-muted-foreground text-sm mt-6 max-w-xl mx-auto leading-relaxed">
            The current enforcement environment reflects heightened antitrust scrutiny across multiple sectors, with increased merger challenges, expanding litigation, and new policy guidance shaping the competitive landscape.
          </p>
        </div>
        </AnimatedSection>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-card rounded-md p-6 shadow-card border border-border">
            <h3 className="font-serif text-lg font-semibold text-foreground mb-6">Enforcement Risk Score Over Time</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={quarterlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 25% 90%)" />
                <XAxis dataKey="quarter" tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="hsl(220 60% 18%)" strokeWidth={2} dot={{ fill: "hsl(38 70% 50%)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card rounded-md p-6 shadow-card border border-border">
            <h3 className="font-serif text-lg font-semibold text-foreground mb-6">Merger Challenges & Litigation</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={quarterlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 25% 90%)" />
                <XAxis dataKey="quarter" tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                <Tooltip />
                <Bar dataKey="challenges" fill="hsl(220 60% 18%)" radius={[2, 2, 0, 0]} />
                <Bar dataKey="litigation" fill="hsl(38 70% 50%)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Factors */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-6">Key Factors</p>
          <div className="space-y-4">
            {factors.map((f) => (
              <div key={f.factor} className="bg-card rounded-md p-6 shadow-card border border-border">
                <h3 className="font-serif text-base font-semibold text-foreground mb-1">{f.factor}</h3>
                <p className="text-muted-foreground text-sm">{f.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </Layout>
);

export default EnforcementIndex;
